import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api, getToken, formatApiError } from "../lib/api";
import StatusBadge, { ocrStatusVariant, docStatusVariant } from "../components/StatusBadge";
import { Folder, ChevronRight, FileText, RefreshCw, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function DocumentExplorer() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [err, setErr] = useState("");
  const [delReason, setDelReason] = useState("");
  const [delOpen, setDelOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, f, dt] = await Promise.all([
        api.get("/teams"),
        api.get("/folders"),
        api.get("/document-types"),
      ]);
      setTeams(t.data);
      setFolders(f.data);
      setDocTypes(dt.data);
    })();
  }, []);

  const loadDocs = async (folderId) => {
    setActiveFolder(folderId);
    const { data } = await api.get("/documents", { params: { folder_id: folderId } });
    setDocs(data);
    setSelected(null);
  };

  const refreshDoc = async () => {
    if (!selected) return;
    const { data } = await api.get(`/documents/${selected.id}`);
    setSelected(data.document);
    if (activeFolder) loadDocs(activeFolder);
  };

  const reprocessOcr = async () => {
    if (!selected) return;
    await api.post(`/documents/${selected.id}/reprocess-ocr`);
    setTimeout(refreshDoc, 1500);
  };

  const requestDeletion = async () => {
    if (!selected) return;
    setErr("");
    try {
      await api.post("/deletion-requests", {
        document_id: selected.id,
        reason: delReason || "User-requested deletion",
      });
      setDelOpen(false);
      setDelReason("");
      refreshDoc();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    }
  };

  const groupedFolders = useMemo(() => {
    const g = {};
    folders.forEach((f) => {
      g[f.team_id] = g[f.team_id] || [];
      g[f.team_id].push(f);
    });
    return g;
  }, [folders]);

  const fileUrl = selected
    ? `${process.env.REACT_APP_BACKEND_URL}/api/documents/${selected.id}/file?auth=${encodeURIComponent(getToken() || "")}`
    : null;
  const isImage = selected && (selected.content_type || "").startsWith("image/");
  const isPdf = selected && (selected.content_type || "") === "application/pdf";

  const canDelete = user.role !== "viewer";

  return (
    <AppShell title="Document Explorer">
      <div className="grid grid-cols-12 gap-px bg-[#E5E5E9] border border-[#E5E5E9] min-h-[70vh]">
        {/* Tree */}
        <aside className="col-span-3 bg-white p-3 overflow-y-auto" data-testid="folder-tree">
          <div className="ds-overline mb-2">Folder tree</div>
          {teams.map((t) => (
            <div key={t.id} className="mb-3">
              <div className="text-[12px] font-semibold tracking-tight">{t.name}</div>
              <ul>
                {(groupedFolders[t.id] || []).map((f) => (
                  <li key={f.id}>
                    <button
                      data-testid={`folder-tree-${f.id}`}
                      onClick={() => loadDocs(f.id)}
                      className={`flex items-center gap-2 w-full text-left px-2 py-1 text-[12.5px] border-l-2 ${
                        activeFolder === f.id
                          ? "border-[#0A0A0A] bg-[#F7F7F8] font-semibold"
                          : "border-transparent hover:bg-[#F7F7F8]"
                      }`}
                    >
                      <Folder size={13} /> {f.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>

        {/* List */}
        <section className="col-span-5 bg-white overflow-y-auto" data-testid="document-list">
          {!activeFolder ? (
            <div className="p-8 text-center text-[#8A8A93]">
              <ChevronRight className="inline" size={14} /> Select a folder to begin.
            </div>
          ) : (
            <table className="ds-table">
              <thead>
                <tr><th>Filename</th><th>Type</th><th>OCR</th><th>Status</th></tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setSelected(d)}
                    className={`cursor-pointer ${selected?.id === d.id ? "bg-[#F7F7F8]" : ""}`}
                    data-testid={`doc-row-${d.id}`}
                  >
                    <td className="font-mono text-[12px] truncate max-w-[260px]">{d.filename}</td>
                    <td>{d.document_type || <span className="text-[#8A8A93]">—</span>}</td>
                    <td><StatusBadge variant={ocrStatusVariant(d.ocr_status)}>{d.ocr_status}</StatusBadge></td>
                    <td><StatusBadge variant={docStatusVariant(d.status)}>{d.status}</StatusBadge></td>
                  </tr>
                ))}
                {docs.length === 0 && <tr><td colSpan={4} className="text-center text-[#8A8A93] py-8">No documents.</td></tr>}
              </tbody>
            </table>
          )}
        </section>

        {/* Preview */}
        <aside className="col-span-4 bg-white p-4 overflow-y-auto" data-testid="document-preview">
          {!selected ? (
            <div className="text-[#8A8A93] text-sm">
              <FileText className="inline" size={14} /> Select a document to preview.
            </div>
          ) : (
            <div>
              <div className="ds-overline">Document</div>
              <div className="font-display text-base font-bold tracking-tight break-words">{selected.filename}</div>
              <div className="ds-overline mt-3" style={{ fontSize: 10 }}>Metadata</div>
              <dl className="text-[12px] mt-1 space-y-1">
                <div><span className="text-[#8A8A93]">Type:</span> {selected.document_type || "—"}</div>
                <div><span className="text-[#8A8A93]">OCR:</span> <StatusBadge variant={ocrStatusVariant(selected.ocr_status)}>{selected.ocr_status}</StatusBadge></div>
                <div><span className="text-[#8A8A93]">Confidence:</span> {(selected.ocr_confidence || 0).toFixed(2)}</div>
                <div><span className="text-[#8A8A93]">Status:</span> <StatusBadge variant={docStatusVariant(selected.status)}>{selected.status}</StatusBadge></div>
                <div><span className="text-[#8A8A93]">Source:</span> {selected.upload_source}</div>
                <div><span className="text-[#8A8A93]">Uploaded by:</span> {selected.uploaded_by_name}</div>
                <div><span className="text-[#8A8A93]">Date:</span> <span className="font-mono">{new Date(selected.upload_date).toLocaleString()}</span></div>
                <div><span className="text-[#8A8A93]">Size:</span> {(selected.size / 1024).toFixed(1)} KB</div>
              </dl>

              <div className="mt-4 flex flex-wrap gap-2">
                <button className="ds-btn-secondary flex items-center gap-1" onClick={reprocessOcr} data-testid="reprocess-ocr-button">
                  <RefreshCw size={12} /> Reprocess OCR
                </button>
                <a className="ds-btn-secondary" href={fileUrl} target="_blank" rel="noreferrer" data-testid="download-document">
                  Download
                </a>
                {canDelete && selected.status === "active" && (
                  <button className="ds-btn-destructive flex items-center gap-1" onClick={() => setDelOpen(true)} data-testid="request-deletion-button">
                    <Trash2 size={12} /> Request deletion
                  </button>
                )}
              </div>

              <div className="ds-overline mt-5" style={{ fontSize: 10 }}>Preview</div>
              <div className="border border-[#E5E5E9] mt-1 bg-[#E8E8EB] min-h-[200px] flex items-center justify-center overflow-hidden">
                {isImage && <img src={fileUrl} alt={selected.filename} className="max-h-80 object-contain" />}
                {isPdf && <iframe title="pdf" src={fileUrl} className="w-full h-80 bg-white" />}
                {!isImage && !isPdf && (
                  <div className="text-[12px] text-[#4A4A52] p-6 text-center">
                    Preview not supported for {selected.content_type}.<br />Use Download.
                  </div>
                )}
              </div>

              <div className="ds-overline mt-5" style={{ fontSize: 10 }}>OCR text</div>
              <div className="font-mono text-[11.5px] whitespace-pre-wrap bg-[#F7F7F8] border border-[#E5E5E9] p-3 max-h-72 overflow-y-auto" data-testid="ocr-text-viewer">
                {selected.ocr_text || <span className="text-[#8A8A93]">No OCR text yet.</span>}
              </div>

              {(selected.ocr_status === "needs_manual_classification" || selected.ocr_status === "failed" || !selected.document_type) && (
                <ManualClassify doc={selected} types={docTypes} onClassified={refreshDoc} />
              )}
            </div>
          )}
        </aside>
      </div>

      {delOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDelOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="ds-card p-6 w-full max-w-md" data-testid="delete-request-form">
            <h3 className="font-display text-lg font-bold mb-2">Request deletion</h3>
            <p className="text-[12px] text-[#4A4A52] mb-3">An admin must approve before this document is deleted.</p>
            <textarea className="ds-input min-h-[90px]" placeholder="Reason" value={delReason} onChange={(e) => setDelReason(e.target.value)} data-testid="delete-reason" />
            {err && <div className="text-[12px] text-[#B42318] mt-2">{err}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <button className="ds-btn-secondary" onClick={() => setDelOpen(false)}>Cancel</button>
              <button className="ds-btn-destructive" onClick={requestDeletion} data-testid="submit-delete-request">Submit request</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function ManualClassify({ doc, types, onClassified }) {
  const [code, setCode] = useState(types[0]?.code || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("document_type_code", code);
      await api.post(`/documents/${doc.id}/classify`, fd);
      onClassified();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 border border-[#FEDF89] bg-[#FFFAEB] p-3" data-testid="manual-classify-panel">
      <div className="ds-overline mb-2" style={{ color: "#B54708" }}>Manual classification needed</div>
      <div className="flex gap-2">
        <select className="ds-input" value={code} onChange={(e) => setCode(e.target.value)} data-testid="manual-classify-select">
          {types.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
        </select>
        <button className="ds-btn-primary" onClick={submit} disabled={busy} data-testid="manual-classify-submit">
          Apply
        </button>
      </div>
    </div>
  );
}
